import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';
import { useTenant } from '../contexts/TenantContext';
import { logger } from '../utils/logger';
import PreorderCreateModal from '../components/pos/PreorderCreateModal';
import PreparationSheetsPanel from '../components/preorders/PreparationSheetsPanel';
import { CalendarIcon, TagIcon, RefreshCwIcon } from 'lucide-react';
import { PrinterAdapterFactory, generatePreorderLabelTicket } from '../services/printers';
import { commentairesPrecommande } from '../utils/commentairesPrecommande';
import { formatQty } from '../utils/pos.utils';

/** Pré-remplissage du modal de création pour « Reconduire ». */
type ReconductData = {
  customerName?: string;
  customerPhone?: string;
  notes?: string;
  pickupDate?: string;
  items?: Array<{
    product_id: string;
    product_name: string;
    quantity: number;
    unit: string;
    price_cents: number;
    sliced: boolean;
  }>;
};

/** Comparaison insensible aux accents et à la casse : « stievenart » doit
 *  trouver « Stiévenart », et « pain épeautre » « Pain Epeautre ». */
function norm(v: string): string {
  return (v || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');
}

/**
 * Regroupe la feuille du boulanger par famille, dans l'ordre servi.
 *
 * Le boulanger lit sa commande par famille — pains, viennoiseries, ciabattas —
 * et pas dans l'ordre où la base rend ses lignes, qui n'en est pas un
 * (signalé pendant une commande, le 10/09/2026). Le serveur a déjà trié :
 * familles à la suite, produits par nom à l'intérieur. Ici on ne fait que
 * refermer les paquets, sans retrier — sinon les deux ordres divergeraient.
 */
const grouperParFamille = (
  lignes: BakerSummary['productsSummary'],
): Array<{ famille: string; lignes: BakerSummary['productsSummary']; total: number }> => {
  const paquets: Array<{ famille: string; lignes: BakerSummary['productsSummary']; total: number }> = [];
  for (const ligne of lignes) {
    // Une ligne classée sur le bon de commande DU FOURNISSEUR ne se range plus
    // dans NOS familles : il lit sa liste dans son ordre, et l'y couper par nos
    // rayons lui redonnerait le désordre qu'il nous a demandé d'enlever. Les
    // lignes non classées gardent leurs familles, en fin de feuille.
    const famille =
      ligne.supplierSortOrder !== null && ligne.supplierSortOrder !== undefined
        ? ''
        : ligne.familyName || 'Autres';
    const dernier = paquets[paquets.length - 1];
    if (dernier && dernier.famille === famille) {
      dernier.lignes.push(ligne);
      dernier.total += ligne.totalQuantity;
    } else {
      paquets.push({ famille, lignes: [ligne], total: ligne.totalQuantity });
    }
  }
  return paquets;
};

interface BakerSummary {
  deliveryDate: string;
  orderDate: string;
  totalItems: number;
  productsSummary: Array<{
    productName: string;
    totalQuantity: number;
    unit: string;
    /** Famille de lecture (catégorie de la fiche) — le serveur trie déjà dessus. */
    familyName?: string;
    /** Référence de l'article chez le fournisseur : c'est par elle qu'il encode. */
    supplierCode?: string | null;
    /** Renseigné = la ligne est à sa place sur SON bon de commande. */
    supplierSortOrder?: number | null;
  }>;
  preordersCount: number;
  /** Fournisseur des fiches (le plus fréquent), imprimé en tête de la feuille. */
  supplierName?: string | null;
}

interface PreorderItem {
  id: string;
  quantity: number;
  prep_options?: string[];
  note?: string | null;
  order_date?: string | null;
  delivery_date?: string | null;
  product: {
    id: string;
    name: string;
    unit?: string;
    preorder_type_id?: string | null;
    prices?: Array<{ amount_cents: number }>;
  };
}

interface Preorder {
  id: string;
  preorder_number: string;
  customer_name: string;
  customer_phone?: string;
  /** Horodatage du SMS « commande prête », automatique ou manuel. */
  notified_at?: string | null;
  customer_email?: string;
  pickup_date: string;
  status: 'PENDING' | 'CONFIRMED' | 'READY' | 'PICKED_UP' | 'CANCELLED';
  source: 'POS' | 'ECOMMERCE' | 'COMMANDE';
  notes?: string;
  /** Commande en ligne d'où vient le pain : son commentaire va sur le sac. */
  linked_order?: { order_number?: string | null; notes?: string | null } | null;
  created_at: string;
  is_editable?: boolean;
  editable_until?: string | null;
  payment_status?: string | null;
  items: PreorderItem[];
}

/**
 * Clôture à la main (27/10/2026) : un pain vendu sans scanner le ticket, ou jamais
 * retiré, laissait la précommande ouverte pour toujours — et « Annuler » est grisé
 * dès que la commande au boulanger est passée. Mêmes règles que le serveur
 * (`cloture-precommande.ts`) : « Retirée » dès le jour du retrait, « Non retirée »
 * une fois ce jour passé et jamais sur une précommande déjà payée en ligne.
 */
const aujourdhuiBelge = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Brussels' });
function issuesPossibles(p: Preorder): { retiree: boolean; nonRetiree: boolean } {
  if (!['PENDING', 'CONFIRMED', 'READY'].includes(p.status)) return { retiree: false, nonRetiree: false };
  const jour = (p.pickup_date || '').slice(0, 10);
  const auj = aujourdhuiBelge();
  return {
    retiree: !!jour && jour <= auj,
    nonRetiree: !!jour && jour < auj && String(p.payment_status || '').toUpperCase() !== 'PAID',
  };
}

interface PreorderType {
  id: string;
  name: string;
  slug: string;
  prep_options: string[];
}

/** Au-delà, les pastilles cèdent la place à une liste déroulante + flèches. */
const MAX_PASTILLES = 8;

const ACTIVE_STATUSES = ['PENDING', 'CONFIRMED', 'READY'];

const STATUS_STYLES: Record<string, string> = {
  PENDING: 'bg-yellow-100 text-yellow-800',
  CONFIRMED: 'bg-blue-100 text-blue-800',
  READY: 'bg-green-100 text-green-800',
  PICKED_UP: 'bg-gray-100 text-gray-800',
  CANCELLED: 'bg-red-100 text-red-800',
};
const STATUS_LABELS: Record<string, string> = {
  PENDING: 'En attente',
  CONFIRMED: 'Confirmée',
  READY: 'Prête',
  PICKED_UP: 'Retirée',
  CANCELLED: 'Annulée',
};

/** Pourquoi une précommande ne se modifie plus — dit sur la fiche, pas dans une alerte. */
function raisonVerrou(p: Preorder): string {
  if (!['PENDING', 'CONFIRMED'].includes(p.status)) {
    return `Statut « ${STATUS_LABELS[p.status] ?? p.status} » : elle ne se modifie plus.`;
  }
  if (p.editable_until) {
    const quand = new Date(p.editable_until).toLocaleString('fr-BE', {
      weekday: 'long', day: 'numeric', month: 'long', hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Brussels',
    });
    return `La commande au producteur est passée (échéance : ${quand}). Pour changer quelque chose, appelez le producteur, puis annulez et recréez-la si besoin.`;
  }
  return 'La commande au producteur est déjà passée.';
}

/**
 * Fiche d'une précommande (27/10/2026). En LECTURE pour une précommande verrouillée —
 * avant, le clic n'ouvrait qu'une alerte et on ne voyait même plus ce que le client
 * avait commandé. En ÉDITION pour une commande client d'épicerie : la fenêtre du pain
 * ne lui convient pas (produits précommandables seulement, jours du boulanger qui
 * déplaceraient la date promise), alors on y corrige quantités, articles, date, remarques.
 */
function FicheConsultation({ preorder, onClose, onReconduire, onEtiquette, onEnregistre, onCloturer }: {
  preorder: Preorder;
  onClose: () => void;
  onReconduire?: () => void;
  onEtiquette: () => void;
  onEnregistre: () => void;
  onCloturer: (issue: 'retiree' | 'non_retiree') => void;
}) {
  const issues = issuesPossibles(preorder);
  const remarqueCommande = preorder.linked_order?.notes?.trim();
  const editable = preorder.source === 'COMMANDE' && !!preorder.is_editable;
  const [lignes, setLignes] = useState(() =>
    preorder.items.map((it) => ({ it, quantite: String(it.quantity).replace('.', ','), retiree: false })),
  );
  const [date, setDate] = useState((preorder.pickup_date || '').slice(0, 10));
  const [notes, setNotes] = useState(preorder.notes || '');
  const [envoi, setEnvoi] = useState(false);
  const lire = (q: string) => Number(String(q).replace(',', '.').trim());
  const gardees = lignes.filter((l) => !l.retiree);
  const souci = !gardees.length
    ? 'Il faut au moins un article (sinon, annulez la commande).'
    : gardees.some((l) => !(lire(l.quantite) > 0))
      ? 'Une quantité est vide ou nulle.'
      : !date ? 'Choisissez la date de retrait.' : null;

  const enregistrer = async () => {
    if (souci || envoi) return;
    setEnvoi(true);
    try {
      await api.patch(`/preorders/${preorder.id}/modify`, {
        pickup_date: date,
        notes,
        items: gardees.map((l) => ({
          product_id: l.it.product.id,
          quantity: lire(l.quantite),
          prep_options: l.it.prep_options || [],
          note: l.it.note ?? undefined,
        })),
      });
      toast.success(`${preorder.preorder_number} modifiée`);
      onEnregistre();
    } catch (e: any) {
      toast.error(e?.response?.data?.message || 'Modification impossible');
      setEnvoi(false);
    }
  };
  return (
    <div className="fixed inset-0 bg-black/50 z-[60] flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="px-5 py-4 border-b flex items-start justify-between gap-3 shrink-0">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-gray-900">{preorder.preorder_number} · {preorder.customer_name}</h2>
            <p className="text-sm text-gray-600">
              Retrait {formatDay(preorder.pickup_date)} · {STATUS_LABELS[preorder.status]}
              {preorder.source === 'COMMANDE' ? ' · commande client' : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800 text-3xl leading-none min-h-[48px] min-w-[48px]" aria-label="Fermer">×</button>
        </div>
        <div className="p-5 space-y-4 overflow-y-auto">
          {!editable && (
            <p className="text-sm bg-gray-100 text-gray-700 rounded-lg px-3 py-2">🔒 {raisonVerrou(preorder)}</p>
          )}
          {editable && (
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">Date de retrait</span>
              <input type="date" value={date} onChange={(e) => setDate(e.target.value)}
                className="mt-1 block rounded-lg border-2 border-gray-300 px-3 py-2" />
            </label>
          )}
          {preorder.customer_phone && (
            <a href={`tel:${preorder.customer_phone}`} className="inline-block text-sky-700 font-medium underline">
              📞 {preorder.customer_phone}
            </a>
          )}
          <ul className="divide-y border rounded-lg">
            {lignes.map(({ it, quantite, retiree }, i) => (
              <li key={it.id} className={`px-3 py-2 ${retiree ? 'bg-gray-50' : ''}`}>
                <div className="flex items-center justify-between gap-3">
                  <span className={retiree ? 'text-gray-400 line-through' : 'text-gray-900'}>{it.product.name}</span>
                  {editable ? (
                    <span className="flex items-center gap-2 shrink-0">
                      {!retiree && (
                        <input type="text" inputMode="decimal" value={quantite} aria-label="Quantité"
                          onChange={(e) => setLignes((ls) => ls.map((l, k) => (k === i ? { ...l, quantite: e.target.value } : l)))}
                          className="w-20 rounded-lg border-2 border-gray-300 px-2 py-1 text-right tabular-nums" />
                      )}
                      <span className="text-sm text-gray-500">{it.product.unit && it.product.unit !== 'pièce' ? it.product.unit : ''}</span>
                      <button type="button" className="text-sm text-red-700 underline min-h-[48px]"
                        onClick={() => setLignes((ls) => ls.map((l, k) => (k === i ? { ...l, retiree: !l.retiree } : l)))}>
                        {retiree ? 'Remettre' : 'Retirer'}
                      </button>
                    </span>
                  ) : (
                    <span className="font-semibold tabular-nums shrink-0">
                      {formatQty(it.quantity)} {it.product.unit && it.product.unit !== 'pièce' ? it.product.unit : ''}
                    </span>
                  )}
                </div>
                {(it.prep_options?.length || it.note) && (
                  <p className="text-sm text-gray-600">
                    {[...(it.prep_options || []), it.note].filter(Boolean).join(' · ')}
                  </p>
                )}
              </li>
            ))}
          </ul>
          {editable ? (
            <label className="block">
              <span className="text-sm font-semibold text-gray-700">Remarques</span>
              <textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={3}
                className="mt-1 block w-full rounded-lg border-2 border-gray-300 px-3 py-2" />
            </label>
          ) : preorder.notes?.trim() && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700">Remarques</h3>
              <p className="text-gray-800 whitespace-pre-line">{preorder.notes}</p>
            </div>
          )}
          {editable && souci && <p className="text-sm text-red-700">{souci}</p>}
          {remarqueCommande && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700">
                Commande en ligne {preorder.linked_order?.order_number ?? ''}
              </h3>
              <p className="text-gray-800 whitespace-pre-line">{remarqueCommande}</p>
            </div>
          )}
        </div>
        <div className="px-5 py-3 border-t flex flex-wrap justify-end gap-2 shrink-0">
          {issues.retiree && (
            <button onClick={() => onCloturer('retiree')} className="px-4 py-2 rounded-lg border-2 border-emerald-600 text-emerald-800 font-medium min-h-[48px]">✓ Retirée</button>
          )}
          {issues.nonRetiree && (
            <button onClick={() => onCloturer('non_retiree')} className="px-4 py-2 rounded-lg border-2 border-amber-500 text-amber-800 font-medium min-h-[48px]">Non retirée</button>
          )}
          <button onClick={onEtiquette} className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-800 font-medium min-h-[48px]">🏷️ Étiquette</button>
          {onReconduire && (
            <button onClick={onReconduire} className="px-4 py-2 rounded-lg border-2 border-gray-300 text-gray-800 font-medium min-h-[48px]">🔁 Reconduire</button>
          )}
          {editable ? (
            <button onClick={enregistrer} disabled={!!souci || envoi}
              className="px-5 py-2 rounded-lg bg-emerald-700 text-white font-semibold min-h-[48px] disabled:opacity-50">
              {envoi ? 'Enregistrement…' : 'Enregistrer'}
            </button>
          ) : (
            <button onClick={onClose} className="px-5 py-2 rounded-lg bg-gray-800 text-white font-semibold min-h-[48px]">Fermer</button>
          )}
        </div>
      </div>
    </div>
  );
}

function formatDay(iso: string): string {
  const d = new Date(iso);
  if (isNaN(d.getTime())) return 'Sans date';
  return d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long' });
}

export default function PreordersOverview() {
  const { tenant } = useTenant();
  const navigate = useNavigate();
  const [preorders, setPreorders] = useState<Preorder[]>([]);
  const [types, setTypes] = useState<PreorderType[]>([]);
  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [showFinished, setShowFinished] = useState(false);
  const [loading, setLoading] = useState(false);
  const [showReconduct, setShowReconduct] = useState(false);
  const [reconductData, setReconductData] = useState<ReconductData | undefined>(undefined);
  const [editId, setEditId] = useState<string | undefined>(undefined);
  const [consultee, setConsultee] = useState<{ preorder: Preorder; aMettreDeCote: boolean } | null>(null);
  const [query, setQuery] = useState('');
  // Une seule journée à l'écran : on prépare et on commande jour par jour.
  const [jourAffiche, setJourAffiche] = useState<string | null>(null);
  // Impressions du jour, rapatriees depuis « Precommandes Pains » : elles ont
  // besoin d'une journee, que cette page decoupe deja.
  const [prepSheets, setPrepSheets] = useState<any>(null);
  const [bakerSummary, setBakerSummary] = useState<BakerSummary | null>(null);
  const [busyDay, setBusyDay] = useState<string | null>(null);

  const load = async () => {
    if (!tenant?.id) return;
    setLoading(true);
    try {
      const headers = { 'X-Tenant-ID': tenant.id };
      const [pre, tp] = await Promise.all([
        api.get('/preorders', { headers }),
        api.get('/preorders/types', { headers }),
      ]);
      setPreorders(Array.isArray(pre) ? pre : []);
      setTypes(Array.isArray(tp) ? tp : []);
    } catch (error) {
      logger.error('Erreur chargement précommandes (overview):', error);
      setPreorders([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenant?.id]);

  const buildItems = (preorder: Preorder) =>
    preorder.items.map((it) => ({
      product_id: it.product.id,
      product_name: it.product.name,
      quantity: it.quantity,
      unit: it.product.unit || 'pièce',
      price_cents: it.product.prices?.[0]?.amount_cents ?? 0,
      sliced: (it.prep_options || []).includes('coupé'),
    }));

  const handleReconduct = (preorder: Preorder) => {
    setEditId(undefined);
    setReconductData({
      customerName: preorder.customer_name,
      customerPhone: preorder.customer_phone,
      notes: preorder.notes,
      items: buildItems(preorder),
    });
    setShowReconduct(true);
  };

  const loadPrepSheets = async (jour: string) => {
    if (!tenant?.id) return;
    setBusyDay(jour);
    setBakerSummary(null);
    try {
      const data: any = await api.get('/preorders/preparation-sheets', {
        headers: { 'X-Tenant-ID': tenant.id },
        params: { deliveryDate: jour, ...(typeFilter !== 'ALL' ? { typeId: typeFilter } : {}) },
      });
      if (!data?.sheets?.length) {
        setPrepSheets(null);
        alert(`Rien à préparer le ${new Date(jour).toLocaleDateString('fr-BE')}.`);
      } else {
        setPrepSheets(data);
      }
    } catch (error: any) {
      alert('❌ ' + (error?.response?.data?.message || error?.message || 'Échec'));
    } finally {
      setBusyDay(null);
    }
  };

  const loadBakerSummary = async (jour: string) => {
    if (!tenant?.id) return;
    setBusyDay(jour);
    setPrepSheets(null);
    try {
      const data: any = await api.get('/preorders/baker-summary/generate', {
        headers: { 'X-Tenant-ID': tenant.id },
        params: { deliveryDate: jour },
      });
      setBakerSummary(data);
    } catch (error: any) {
      alert('❌ ' + (error?.response?.data?.message || error?.message || 'Échec'));
    } finally {
      setBusyDay(null);
    }
  };

  /**
   * Ouvre l'imprimante EPSON de CE poste. Rendue nulle si le bridge local est
   * absent : la page est aussi consultable depuis un téléphone, où il n'y a
   * évidemment aucune imprimante branchée.
   */
  const ouvrirImprimante = async () => {
    const dispo = await PrinterAdapterFactory.checkHardwareBridge();
    if (!dispo) {
      alert(
        "❌ Imprimante indisponible.\n\nLes étiquettes s'impriment sur l'EPSON de la caisse : " +
          'cette page doit être ouverte sur un poste de caisse.',
      );
      return null;
    }
    const printerConfig = (tenant?.config as any)?.hardware?.printer || {};
    const printer = await PrinterAdapterFactory.createAsync({
      model: printerConfig.type || 'BROWSER_PRINT',
      connection: 'USB',
      settings: printerConfig.settings,
    });
    await printer.connect();
    return printer;
  };

  const printAllLabels = async (jour: string) => {
    // On imprime ce qui est À L'ÉCRAN pour cette journée, et non « les
    // précommandes dont la date de retrait tombe ce jour-là » comme le faisait
    // l'ancien appel serveur. Un pain cuit le vendredi et retiré le samedi doit
    // recevoir son étiquette LE VENDREDI, au moment où le sac est constitué.
    const entrees = groups.get(jour) ?? [];
    if (entrees.length === 0) return;

    const ok = window.confirm(
      `Imprimer ${entrees.length} étiquette(s) pour le ${new Date(jour).toLocaleDateString('fr-BE')} ?`,
    );
    if (!ok) return;

    setBusyDay(jour);
    try {
      const printer = await ouvrirImprimante();
      if (!printer) return;

      let echecs = 0;
      for (const e of entrees) {
        try {
          await printer.print(
            generatePreorderLabelTicket({
              preorderNumber: e.preorder.preorder_number,
              customerName: e.preorder.customer_name,
              pickupDate: e.preorder.pickup_date,
              aMettreDeCote: e.aMettreDeCote,
              commentaires: commentairesPrecommande(e.preorder),
            }),
          );
        } catch {
          echecs++;
        }
      }
      await printer.disconnect();

      alert(
        echecs === 0
          ? `✅ ${entrees.length} étiquette(s) imprimée(s)`
          : `⚠️ ${entrees.length - echecs}/${entrees.length} imprimée(s), ${echecs} échec(s)`,
      );
    } catch (error: any) {
      alert('❌ ' + (error?.message || 'Échec de l\'impression'));
    } finally {
      setBusyDay(null);
    }
  };

  const printBakerSummary = () => {
    if (!bakerSummary) return;

    const printWindow = window.open('', '_blank');
    if (!printWindow) return;

    const html = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Commande Boulanger - ${new Date(bakerSummary.deliveryDate).toLocaleDateString('fr-FR')}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 20px; }
            h1 { color: #10b981; border-bottom: 2px solid #10b981; padding-bottom: 10px; }
            .info { margin: 20px 0; }
            .info strong { display: inline-block; width: 150px; }
            table { width: 100%; border-collapse: collapse; margin-top: 20px; }
            th, td { border: 1px solid #ddd; padding: 12px; text-align: left; }
            th { background-color: #10b981; color: white; }
            tr:nth-child(even) { background-color: #f9f9f9; }
            /* Pas de totaux sur le bon — ni sous-total de groupe, ni total
               général (demande du 14/09/2026). Le boulanger fabrique ce qu'il lit
               ligne à ligne ; un cumul n'entre dans aucune de ses fournées et
               n'ajoute qu'un chiffre à vérifier. Les comptes restent à l'écran,
               côté magasin, où ils servent.
               L'intitulé de groupe ne subsiste que pour les articles PAS ENCORE
               référencés chez lui : eux ont besoin d'être signalés. */
            .famille td { background-color: #d1fae5 !important; font-weight: bold;
                          text-transform: uppercase; letter-spacing: .04em; }
            @media print {
              button { display: none; }
            }
          </style>
        </head>
        <body>
          <h1>📋 Commande Boulanger</h1>
          <div class="info">
            <!-- Fournisseur écrit en toutes lettres : cette feuille est scannée à la
                 réception, et sans nom la lecture automatique en inventait un
                 (« Boulanger (foliesboutik.be) », 30/09/2026). -->
            ${bakerSummary.supplierName ? `<div><strong>Fournisseur:</strong> ${bakerSummary.supplierName}</div>` : ''}
            <div><strong>Date de livraison:</strong> ${new Date(bakerSummary.deliveryDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <div><strong>Date de commande:</strong> ${new Date(bakerSummary.orderDate).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' })}</div>
            <!-- Pas de « nombre de précommandes » ici : le boulanger fabrique des
                 quantités, pas des clients. Savoir que ces 12 croissants viennent
                 de trois clients ou de neuf ne change rien à sa fournée, et ça
                 encombre l'en-tête de la feuille qu'il a en main (10/09/2026).
                 Le compte reste à l'écran, côté magasin, où il sert. -->
          </div>

          <table>
            <thead>
              <tr>
                <th>Code</th>
                <th>Produit</th>
                <th>Quantité</th>
                <th>Unité</th>
              </tr>
            </thead>
            <tbody>
              ${grouperParFamille(bakerSummary.productsSummary)
                .map(
                  (g) => `
                ${g.famille ? `
                <tr class="famille">
                  <td colspan="4">${g.famille}</td>
                </tr>` : ''}
                ${g.lignes
                  .map(
                    (p) => `
                <tr>
                  <td>${p.supplierCode || ''}</td>
                  <td>${p.productName}</td>
                  <td>${p.totalQuantity}</td>
                  <td>${p.unit}</td>
                </tr>`,
                  )
                  .join('')}`,
                )
                .join('')}

            </tbody>
          </table>

          <button onclick="window.print()" style="margin-top: 20px; padding: 10px 20px; background: #10b981; color: white; border: none; border-radius: 5px; cursor: pointer;">
            Imprimer
          </button>
        </body>
      </html>
    `;

    printWindow.document.write(html);
    printWindow.document.close();
  };

  const handleModify = (preorder: Preorder) => {
    setEditId(preorder.id);
    setReconductData({
      customerName: preorder.customer_name,
      customerPhone: preorder.customer_phone,
      notes: preorder.notes,
      pickupDate: (preorder.pickup_date || '').slice(0, 10),
      items: buildItems(preorder),
    });
    setShowReconduct(true);
  };

  /**
   * Prévenir le client par SMS, à la main.
   *
   * L'envoi automatique ne part qu'une fois, et seulement si le client a coché
   * la case à la prise de commande. Ce bouton couvre tout le reste : le client
   * qui n'a rien coché mais qu'on veut joindre, la commande prête la veille,
   * celui qui n'est pas passé. La confirmation affiche le numéro ET le texte —
   * un SMS part chez quelqu'un et coûte de l'argent, on ne l'envoie pas d'un
   * appui distrait.
   */
  const handleNotifySms = async (preorder: Preorder) => {
    if (!tenant?.id) return;
    if (!preorder.customer_phone) {
      alert(`❌ Aucun numéro de téléphone sur ${preorder.preorder_number}.`);
      return;
    }
    const dejaPrevenu = !!preorder.notified_at;
    const ok = window.confirm(
      `Envoyer un SMS à ${preorder.customer_name} (${preorder.customer_phone}) ?\n\n` +
        '« Bonjour, votre commande de pain est prete. Retrait le… ' +
        'Les Folies Maraicheres »\n\n' +
        (dejaPrevenu ? '⚠️ Ce client a DÉJÀ été prévenu pour cette commande.' : ''),
    );
    if (!ok) return;

    setBusyDay(preorder.id);
    try {
      const res: any = await api.post(
        `/preorders/${preorder.id}/notify-sms`,
        {},
        { headers: { 'X-Tenant-ID': tenant.id } },
      );
      if (res?.sent) {
        alert(`✅ SMS envoyé à ${res.phone}`);
      } else {
        // Mode simulation compris : le dire, plutôt que d'annoncer un envoi
        // qui n'a pas eu lieu.
        alert(`⚠️ SMS non envoyé : ${res?.reason || 'raison inconnue'}`);
      }
      load();
    } catch (error: any) {
      alert('❌ ' + (error?.response?.data?.message || error?.message || 'Échec'));
    } finally {
      setBusyDay(null);
    }
  };

  const handleCloturer = async (preorder: Preorder, issue: 'retiree' | 'non_retiree') => {
    const ok = window.confirm(
      issue === 'retiree'
        ? `${preorder.preorder_number} — ${preorder.customer_name}\n\nMarquer comme RETIRÉE ?\n\nÀ utiliser quand le pain est parti sans scanner le ticket. Rien n'est encaissé ni sorti du stock ici : la vente l'a déjà fait.`
        : `${preorder.preorder_number} — ${preorder.customer_name}\n\nMarquer comme NON RETIRÉE ?\n\nLe client n'est pas venu. Le pain reste en stock : vendez-le ou passez-le en perte comme d'habitude.`,
    );
    if (!ok) return;
    try {
      await api.patch(`/preorders/${preorder.id}/cloturer`, { issue });
      setConsultee(null);
      load();
    } catch (error: any) {
      alert('❌ ' + (error?.response?.data?.message || error?.message || 'Clôture impossible'));
    }
  };

  const handleCancel = async (preorder: Preorder) => {
    if (!tenant?.id) return;
    // Une confirmation qui n'annonce qu'un numéro ne permet pas de vérifier qu'on
    // annule la bonne : le client, la date et le contenu doivent y figurer.
    const articles = preorder.items
      .map((it) => `  • ${it.quantity} × ${it.product.name}`)
      .join('\n');
    const ok = window.confirm(
      `Annuler la précommande ${preorder.preorder_number} ?\n\n` +
        `${preorder.customer_name}\n` +
        `Retrait le ${new Date(preorder.pickup_date).toLocaleDateString('fr-BE', {
          weekday: 'long',
          day: 'numeric',
          month: 'long',
        })}\n\n` +
        `${articles}\n\n` +
        `Cette annulation est définitive.`,
    );
    if (!ok) return;
    try {
      await api.patch(`/preorders/${preorder.id}/cancel`, {}, { headers: { 'X-Tenant-ID': tenant.id } });
      load();
    } catch (error: any) {
      alert('❌ ' + (error?.response?.data?.message || error?.message || 'Échec de l\'annulation'));
    }
  };

  const printLabel = async (preorder: Preorder, aMettreDeCote = false) => {
    try {
      const printer = await ouvrirImprimante();
      if (!printer) return;
      await printer.print(
        generatePreorderLabelTicket({
          preorderNumber: preorder.preorder_number,
          customerName: preorder.customer_name,
          pickupDate: preorder.pickup_date,
          aMettreDeCote,
          commentaires: commentairesPrecommande(preorder),
        }),
      );
      await printer.disconnect();
    } catch (error: any) {
      alert('❌ ' + (error?.message || 'Échec de l\'impression'));
    }
  };

  // Filtrage
  // La recherche porte sur ce qu'on a réellement sous la main au comptoir : le
  // nom du client, son téléphone, le numéro de la précommande — et le nom des
  // produits, pour retrouver « qui a commandé du seigle ».
  const termes = norm(query).split(/\s+/).filter(Boolean);
  const visible = preorders.filter((p) => {
    const activeOk = showFinished || ACTIVE_STATUSES.includes(p.status);
    const typeOk =
      typeFilter === 'ALL' || p.items.some((it) => it.product.preorder_type_id === typeFilter);
    if (!activeOk || !typeOk) return false;
    if (termes.length === 0) return true;
    const foin = norm(
      [
        p.preorder_number,
        p.customer_name,
        p.customer_phone || '',
        p.notes || '',
        ...p.items.map((it) => it.product.name),
      ].join(' '),
    );
    // Tous les mots doivent être présents : « manu pain » ne remonte que ses pains.
    return termes.every((t) => foin.includes(t));
  });

  // Regroupement par JOURNÉE, pas par date de retrait.
  //
  // Une précommande dont le pain sort du four le vendredi mais qui n'est retirée
  // que le samedi doit apparaître les DEUX jours : le vendredi pour être mise de
  // côté à la livraison, le samedi pour être remise au client. Grouper sur le
  // seul `pickup_date` la faisait disparaître du vendredi — jour où il y a
  // pourtant du travail (cas PRE-2026-001, 18/08/2026).
  type Entree = {
    preorder: Preorder;
    /** Lignes livrées CE jour-là (vide si la commande n'est là que pour le retrait). */
    lignes: PreorderItem[];
    estRetrait: boolean;
    /** Livré ce jour mais retiré plus tard → à mettre de côté. */
    aMettreDeCote: boolean;
  };

  const groups = new Map<string, Entree[]>();
  const pousser = (jour: string, e: Entree) => {
    if (!groups.has(jour)) groups.set(jour, []);
    groups.get(jour)!.push(e);
  };

  for (const p of visible) {
    const retrait = (p.pickup_date || '').slice(0, 10) || null;
    const parJour = new Map<string, PreorderItem[]>();
    for (const it of p.items) {
      const j = (it.delivery_date || '').slice(0, 10);
      if (j) parJour.set(j, [...(parJour.get(j) ?? []), it]);
    }

    for (const [jour, lignes] of parJour) {
      pousser(jour, {
        preorder: p,
        lignes,
        estRetrait: jour === retrait,
        aMettreDeCote: !!retrait && jour < retrait,
      });
    }

    // Le jour du retrait doit figurer même si rien n'y est livré (tout est déjà
    // en réserve), et les commandes sans date de livraison gardent leur repère.
    if (!parJour.has(retrait ?? '')) {
      pousser(retrait ?? 'sans-date', {
        preorder: p,
        lignes: [],
        estRetrait: !!retrait,
        aMettreDeCote: false,
      });
    }
  }
  const sortedDates = Array.from(groups.keys()).sort();

  // La journée affichée reste valide au fil des rechargements ; si elle
  // disparaît (filtre, recherche), on retombe sur la première disponible.
  const jourCourant =
    jourAffiche && sortedDates.includes(jourAffiche) ? jourAffiche : sortedDates[0] ?? null;

  // Synthèse « à commander » du jour : uniquement ce qui est LIVRÉ ce jour-là.
  // Y mettre toutes les lignes de chaque commande ferait compter deux fois les
  // pains d'une commande étalée sur deux cuissons.
  const summarize = (list: Entree[]) => {
    const m = new Map<string, { name: string; unit: string; qty: number }>();
    for (const e of list) {
      // Commande client d'épicerie : rien à commander au boulanger (comme la
      // feuille du serveur, `source: { not: 'COMMANDE' }`).
      if (e.preorder.source === 'COMMANDE') continue;
      for (const it of e.lignes) {
        if (typeFilter !== 'ALL' && it.product.preorder_type_id !== typeFilter) continue;
        const cur = m.get(it.product.id) || { name: it.product.name, unit: it.product.unit || 'pièce', qty: 0 };
        cur.qty += it.quantity;
        m.set(it.product.id, cur);
      }
    }
    return Array.from(m.values()).sort((a, b) => a.name.localeCompare(b.name));
  };

  // Compteurs de statut
  const counts = { PENDING: 0, CONFIRMED: 0, READY: 0 };
  visible.forEach((p) => {
    if (p.status in counts) (counts as any)[p.status]++;
  });

  return (
    <div className="max-w-7xl mx-auto p-6">
      <button onClick={() => navigate(-1)} className="mb-3 text-sm text-gray-500 hover:text-gray-800">
        ← Retour
      </button>
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Précommandes — vue d'ensemble</h1>
        <p className="mt-2 text-gray-600">
          Toutes les précommandes à venir, regroupées par jour de retrait. Reconduire, modifier ou annuler.
        </p>
      </div>

      {/* Barre de filtres + compteurs */}
      <div className="bg-white rounded-lg shadow p-4 mb-6 flex flex-wrap items-center gap-4">
        <div className="flex-1 min-w-[220px]">
          <label className="block text-xs font-medium text-gray-500 mb-1">Rechercher</label>
          <div className="relative">
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Client, téléphone, n° ou produit…"
              className="w-full border border-gray-300 rounded-md px-3 py-2 pr-8 min-h-[48px]"
              autoComplete="off"
            />
            {query && (
              <button
                onClick={() => setQuery('')}
                className="absolute inset-y-0 right-0 px-2 text-gray-400 hover:text-gray-700"
                title="Effacer"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Type de produit</label>
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value)}
            className="border border-gray-300 rounded-md px-3 py-2"
          >
            <option value="ALL">Tous les types</option>
            {types.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
        </div>

        <label className="flex items-center gap-2 text-sm text-gray-700 mt-4">
          <input type="checkbox" checked={showFinished} onChange={(e) => setShowFinished(e.target.checked)} />
          Afficher aussi retirées / annulées
        </label>

        <div className="ml-auto flex flex-wrap items-center gap-2 sm:gap-3 text-sm mt-4">
          <span className="whitespace-nowrap px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">⏳ {counts.PENDING} en attente</span>
          <span className="whitespace-nowrap px-2 py-1 rounded-full bg-blue-100 text-blue-800">✓ {counts.CONFIRMED} confirmées</span>
          <span className="whitespace-nowrap px-2 py-1 rounded-full bg-green-100 text-green-800">📦 {counts.READY} prêtes</span>
          <button
            onClick={load}
            className="p-2 text-gray-500 hover:text-gray-800"
            title="Rafraîchir"
          >
            <RefreshCwIcon className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Sélecteur de journée. On prépare et on commande jour par jour : tout
          empiler obligeait à faire défiler, et les boutons d'impression d'une
          journée se retrouvaient loin du panneau qu'ils ouvrent, en haut de page. */}
      {sortedDates.length > 1 && (
        <div className="flex items-center gap-2 flex-wrap mb-4">
          {/* Au-delà de MAX_PASTILLES, la rangée deviendrait une bouillie — il
              suffit d'afficher les retirées et annulées pour voir remonter tout
              l'historique. On bascule alors sur une liste déroulante, qui reste
              lisible quel que soit le nombre de journées. */}
          {sortedDates.length <= MAX_PASTILLES ? (
            sortedDates.map((d) => {
              const actif = d === jourCourant;
              const n = (groups.get(d) ?? []).length;
              return (
                <button
                  key={d}
                  onClick={() => setJourAffiche(d)}
                  className={`px-4 py-2 rounded-full text-sm font-semibold border min-h-[48px] capitalize transition-colors ${
                    actif
                      ? 'bg-emerald-600 border-emerald-600 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:bg-gray-50'
                  }`}
                >
                  {d === 'sans-date' ? 'Sans date' : formatDay(d)} ({n})
                </button>
              );
            })
          ) : (
            <>
              <button
                onClick={() => {
                  const i = sortedDates.indexOf(jourCourant ?? '');
                  if (i > 0) setJourAffiche(sortedDates[i - 1]);
                }}
                disabled={sortedDates.indexOf(jourCourant ?? '') <= 0}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white disabled:opacity-30 min-h-[48px]"
                title="Journée précédente"
              >
                ←
              </button>
              <select
                value={jourCourant ?? ''}
                onChange={(e) => setJourAffiche(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg bg-white min-h-[48px] capitalize"
              >
                {sortedDates.map((d) => (
                  <option key={d} value={d}>
                    {d === 'sans-date' ? 'Sans date' : formatDay(d)} ({(groups.get(d) ?? []).length})
                  </option>
                ))}
              </select>
              <button
                onClick={() => {
                  const i = sortedDates.indexOf(jourCourant ?? '');
                  if (i >= 0 && i < sortedDates.length - 1) setJourAffiche(sortedDates[i + 1]);
                }}
                disabled={sortedDates.indexOf(jourCourant ?? '') >= sortedDates.length - 1}
                className="px-3 py-2 rounded-lg border border-gray-300 bg-white disabled:opacity-30 min-h-[48px]"
                title="Journée suivante"
              >
                →
              </button>
              <span className="text-sm text-gray-500">
                {sortedDates.length} journées
              </span>
            </>
          )}
        </div>
      )}

      {prepSheets && (
        <PreparationSheetsPanel data={prepSheets} onClose={() => setPrepSheets(null)} />
      )}

      {bakerSummary && (
        <div className="bg-emerald-50 border-2 border-emerald-200 rounded-lg p-6 mb-6">
          <div className="flex items-center justify-between mb-4 gap-3">
            <h2 className="text-xl font-bold text-emerald-900">
              📋 À commander pour le{' '}
              {new Date(bakerSummary.deliveryDate).toLocaleDateString('fr-BE', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
              })}
            </h2>
            <div className="flex gap-2">
              <button
                onClick={printBakerSummary}
                className="px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-md text-sm font-medium"
              >
                Imprimer
              </button>
              <button
                onClick={() => setBakerSummary(null)}
                className="px-3 py-2 bg-white border border-emerald-300 text-emerald-800 rounded-md text-sm"
              >
                Fermer
              </button>
            </div>
          </div>
          <p className="text-sm text-emerald-800 mb-3">
            {bakerSummary.preordersCount} précommande{bakerSummary.preordersCount > 1 ? 's' : ''} ·{' '}
            {bakerSummary.totalItems} article{bakerSummary.totalItems > 1 ? 's' : ''} — à commander pour le{' '}
            {new Date(bakerSummary.orderDate).toLocaleDateString('fr-BE')}
          </p>
          <div className="bg-white rounded-lg divide-y">
            {grouperParFamille(bakerSummary.productsSummary).map((g) => (
              <div key={g.famille}>
                {g.famille && (
                  <div className="px-4 py-1.5 bg-emerald-100 text-xs font-bold uppercase tracking-wide text-emerald-900">
                    {g.famille}
                  </div>
                )}
                {g.lignes.map((p) => (
                  <div
                    key={p.productName}
                    className="flex justify-between px-4 py-2 text-sm border-t border-gray-100"
                  >
                    <span>
                      {p.supplierCode && (
                        <span className="mr-2 inline-block min-w-[2.2rem] rounded bg-gray-100 px-1.5 py-0.5 text-center text-xs font-bold tabular-nums text-gray-600">
                          {p.supplierCode}
                        </span>
                      )}
                      {p.productName}
                    </span>
                    <span className="font-semibold tabular-nums">
                      {p.totalQuantity} {p.unit}
                    </span>
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      )}

      {loading ? (
        <div className="p-12 text-center text-gray-500">
          <RefreshCwIcon className="w-8 h-8 mx-auto mb-2 animate-spin" />
          Chargement...
        </div>
      ) : sortedDates.length === 0 ? (
        <div className="p-12 text-center text-gray-500 bg-white rounded-lg shadow">
          {query
            ? `Aucune précommande ne correspond à « ${query} ».`
            : 'Aucune précommande à afficher'}
        </div>
      ) : (
        <div className="space-y-8">
          {sortedDates.filter((d) => d === jourCourant).map((dateKey) => {
            const list = groups.get(dateKey)!;
            const summary = summarize(list);
            return (
              <section key={dateKey}>
                <div className="flex items-center gap-2 mb-3 flex-wrap">
                  <CalendarIcon className="w-5 h-5 text-emerald-600" />
                  <h2 className="text-xl font-bold text-gray-900 capitalize">
                    {dateKey === 'sans-date' ? 'Sans date' : formatDay(dateKey)}
                  </h2>
                  <span className="text-sm text-gray-500">({list.length} précommande{list.length > 1 ? 's' : ''})</span>

                  {/* Impressions de CETTE journée. Rapatriées de « Précommandes
                      Pains » : le jour se lit dans la section, il n'y a plus de
                      sélecteur de date à accorder avec la liste. */}
                  {dateKey !== 'sans-date' && (
                    <div className="ml-auto flex flex-wrap gap-2">
                      <button
                        onClick={() => loadPrepSheets(dateKey)}
                        disabled={busyDay === dateKey}
                        className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium min-h-[48px]"
                        title="Fiches de préparation de ce jour, une par commande"
                      >
                        🧾 Fiches
                      </button>
                      <button
                        onClick={() => loadBakerSummary(dateKey)}
                        disabled={busyDay === dateKey}
                        className="px-3 py-1.5 bg-emerald-600 hover:bg-emerald-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium min-h-[48px]"
                        title="Quantités à commander au producteur pour ce jour"
                      >
                        📋 Commande producteur
                      </button>
                      <button
                        onClick={() => printAllLabels(dateKey)}
                        disabled={busyDay === dateKey}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 disabled:bg-gray-300 text-white rounded-md text-sm font-medium min-h-[48px]"
                        title="Imprimer sur l'EPSON de la caisse une étiquette par précommande de ce jour (nom du client + code-barres)"
                      >
                        🏷️ Étiquettes
                      </button>
                    </div>
                  )}
                </div>

                {/* À commander (synthèse producteur inline) */}
                {summary.length > 0 && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4 mb-3">
                    <h3 className="text-sm font-semibold text-emerald-900 mb-2">📋 À commander au producteur</h3>
                    <div className="flex flex-wrap gap-x-6 gap-y-1">
                      {summary.map((s) => (
                        <span key={s.name} className="text-sm text-emerald-900">
                          {s.name} : <strong>{s.qty}</strong> {s.unit}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {/* Liste simple : une ligne par precommande, edition au clic.
                    Le detail (articles, notes, telephone) est deja dans le modal
                    d'edition — le repeter ici transformait la liste en pave. */}
                <div className="bg-white rounded-lg shadow divide-y">
                  {list.map((entree) => {
                    const preorder = entree.preorder;
                    // Le compteur affiche ce qui concerne CE jour : les articles
                    // livrés, ou la commande entière le jour du retrait.
                    const nbArticles = (entree.lignes.length ? entree.lignes : preorder.items).reduce(
                      (n, it) => n + Number(it.quantity || 0),
                      0,
                    );
                    const modifiable = !!preorder.is_editable;
                    return (
                      <div
                        key={`${preorder.id}:${entree.estRetrait ? 'retrait' : 'livraison'}`}
                        className={`flex items-center gap-2 px-3 py-2 hover:bg-gray-50 ${
                          entree.aMettreDeCote ? 'bg-amber-50/50' : ''
                        }`}
                      >
                        <button
                          onClick={() =>
                            modifiable && preorder.source !== 'COMMANDE'
                              ? handleModify(preorder)
                              : setConsultee({ preorder, aMettreDeCote: entree.aMettreDeCote })
                          }
                          className="flex-1 min-w-0 text-left flex items-center gap-2 min-h-[48px]"
                          title={modifiable ? 'Modifier cette précommande' : 'Voir cette précommande (commande producteur déjà passée)'}
                        >
                          <span className="font-semibold text-gray-900 shrink-0">
                            {preorder.preorder_number}
                          </span>
                          <span className="text-gray-700 truncate">{preorder.customer_name}</span>
                          <span className="text-sm text-gray-500 shrink-0">
                            · {nbArticles} art.
                          </span>
                          {/* La consigne du jour prime sur le statut : c'est elle
                              qui dit quoi faire du sac une fois préparé. */}
                          {entree.aMettreDeCote && (
                            <span className="ml-auto shrink-0 text-xs font-bold px-2 py-0.5 rounded bg-amber-200 text-amber-900">
                              📦 de côté — retrait le{' '}
                              {new Date(preorder.pickup_date).toLocaleDateString('fr-BE')}
                            </span>
                          )}
                          <span
                            className={`${entree.aMettreDeCote ? 'ml-2' : 'ml-auto'} shrink-0 text-xs px-2 py-0.5 rounded ${STATUS_STYLES[preorder.status]}`}
                          >
                            {STATUS_LABELS[preorder.status]}
                          </span>
                          {!modifiable && (
                            <span className="shrink-0 text-xs text-gray-400" title="Commande producteur déjà passée">
                              🔒
                            </span>
                          )}
                        </button>

                        {/* Actions secondaires. LIBELLÉES : une icône seule n'a pas
                            d'infobulle sur un écran tactile — on ne découvre ce
                            qu'elle fait qu'en appuyant, ce qui n'est pas acceptable
                            pour une annulation. */}
                        <div className="flex items-center gap-1 shrink-0">
                          {preorder.source !== 'COMMANDE' && (
                          <button
                            onClick={() => handleReconduct(preorder)}
                            className="px-2 py-2 rounded-md hover:bg-gray-100 text-sm text-gray-700 min-h-[48px] whitespace-nowrap"
                            title="Créer une nouvelle précommande identique, à une autre date"
                          >
                            🔁 Reconduire
                          </button>
                          )}
                          <button
                            onClick={() => printLabel(preorder, entree.aMettreDeCote)}
                            className="px-2 py-2 rounded-md hover:bg-gray-100 text-sm text-gray-700 min-h-[48px] whitespace-nowrap flex items-center gap-1"
                            title="Imprimer l'étiquette (nom du client + code-barres) sur l'EPSON de la caisse"
                          >
                            <TagIcon className="w-4 h-4" /> Étiquette
                          </button>
                          {preorder.customer_phone && (
                            <button
                              onClick={() => handleNotifySms(preorder)}
                              className={`px-2 py-2 rounded-md hover:bg-gray-100 text-sm min-h-[48px] whitespace-nowrap ${
                                preorder.notified_at ? 'text-gray-400' : 'text-gray-700'
                              }`}
                              title={
                                preorder.notified_at
                                  ? `Client déjà prévenu — renvoyer un SMS au ${preorder.customer_phone}`
                                  : `Prévenir ${preorder.customer_name} par SMS au ${preorder.customer_phone}`
                              }
                            >
                              {preorder.notified_at ? '✓ SMS' : '💬 SMS'}
                            </button>
                          )}
                          {issuesPossibles(preorder).retiree && (
                            <button
                              onClick={() => handleCloturer(preorder, 'retiree')}
                              className="px-2 py-2 rounded-md hover:bg-emerald-50 text-sm text-emerald-800 min-h-[48px] whitespace-nowrap"
                              title="Le pain est parti sans scanner le ticket : clôturer la précommande"
                            >
                              ✓ Retirée
                            </button>
                          )}
                          {issuesPossibles(preorder).nonRetiree && (
                            <button
                              onClick={() => handleCloturer(preorder, 'non_retiree')}
                              className="px-2 py-2 rounded-md hover:bg-amber-50 text-sm text-amber-800 min-h-[48px] whitespace-nowrap"
                              title="Le client n'est pas venu : clôturer la précommande"
                            >
                              Non retirée
                            </button>
                          )}
                          {ACTIVE_STATUSES.includes(preorder.status) && !issuesPossibles(preorder).nonRetiree && (
                            <button
                              onClick={() => handleCancel(preorder)}
                              disabled={!modifiable}
                              className="px-2 py-2 rounded-md hover:bg-red-50 text-sm text-red-700 disabled:opacity-30 min-h-[48px] whitespace-nowrap"
                              title={modifiable ? 'Annuler cette précommande' : 'Commande producteur déjà passée'}
                            >
                              ✖️ Annuler
                            </button>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            );
          })}
        </div>
      )}

      {consultee && (
        <FicheConsultation
          preorder={consultee.preorder}
          onClose={() => setConsultee(null)}
          onEnregistre={() => { setConsultee(null); load(); }}
          onCloturer={(issue) => handleCloturer(consultee.preorder, issue)}
          onEtiquette={() => printLabel(consultee.preorder, consultee.aMettreDeCote)}
          onReconduire={
            consultee.preorder.source !== 'COMMANDE'
              ? () => { const p = consultee.preorder; setConsultee(null); handleReconduct(p); }
              : undefined
          }
        />
      )}

      {/* Reconduire : ré-ouvre le modal de création pré-rempli, date neuve */}
      <PreorderCreateModal
        open={showReconduct}
        onClose={() => {
          setShowReconduct(false);
          setReconductData(undefined);
          setEditId(undefined);
          load();
        }}
        initialData={reconductData}
        editPreorderId={editId}
      />
    </div>
  );
}
